import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { GraphQLClient } from "graphql-request";

import Header from "../../components/Header";
import StatusChip from "../../components/StatusChip";
import { projectStatusTone } from "../../status/StatusChipTones";
import { formatEnum } from "../../utils/formatEnum";
import DeleteProjectModal from "../../components/DeleteProjectModal";
import TableActions from "../../components/TableActions";
import ActionButton from "../../components/ActionButton";
import ThemedDataGrid from "../../components/ThemedDataGrid";
import { useSnackbar } from "../../components/snackbarProvider.jsx";
import { graphqlEndpoint } from "../../config";

import { READ_PROJECTS_FILTER } from "../../graph/projects/queries";
import { DELETE_PROJECT } from "../../graph/projects/mutations";
import { useTenant } from "../../context/TenantContext";

const client = new GraphQLClient(graphqlEndpoint);

const ManageProjects = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantId, tenantName } = useTenant();
  const { setSnackbar } = useSnackbar();

  const { sop = false, name = sop ? "SOP" : "Project" } = location.state || {};

  const [projects, setProjects] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  const redirectPath = sop ? "/manageSOPs" : "/manageProjects";

  const fetchProjects = useCallback(async () => {
    if (!tenantId) {
      setProjects([]);
      return;
    }

    try {
      const data = await client.request(READ_PROJECTS_FILTER, {
        filter: {
          tenantId,
          isSOPTemplate: sop,
        },
      });

      const formattedProjects = (data.readProjectsFilter || []).map((project) => ({
        ...project,
        id: project.id,
        labels: project.labels?.join(", ") || "",
        taskCount: project.tasks?.length || 0,
        sourceProjectTitle: project.sourceProject?.title || "—",
      }));

      setProjects(formattedProjects);
    } catch (err) {
      console.error("Failed to fetch projects:", err);
      setProjects([]);
      setSnackbar({
        open: true,
        severity: "error",
        message: `Failed to load ${String(name).toLowerCase()}s`,
      });
    }
  }, [tenantId, sop, name, setSnackbar]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleOpenDeleteModal = (project) => {
    setSelectedProject(project);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedProject(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedProject?.id) return;

    try {
      await client.request(DELETE_PROJECT, { id: selectedProject.id });
      await fetchProjects();
      handleCloseDeleteModal();

      setSnackbar({
        open: true,
        severity: "success",
        message: `${name} deleted successfully`,
      });
    } catch (err) {
      console.error("Failed to delete project:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: `Failed to delete ${String(name).toLowerCase()}`,
      });
    }
  };

  const handleEdit = (id) => {
    navigate(`/projects/edit/${id}`, {
      state: {
        sop,
        name,
        redirectPath,
      },
    });
  };

  const handleCosts = (id) => {
    navigate(`/manageProjects/${id}/costs`);
  };

  const columns = [
    { field: "title", headerName: "Title", flex: 1.2 },
    {
      field: "status",
      headerName: "Status",
      flex: 0.8,
      renderCell: ({ value }) => (
        <StatusChip
          label={formatEnum(value)}
          tone={projectStatusTone(value)}
        />
      ),
    },
    { field: "dueDate", headerName: "Due Date", flex: 0.9 },
    { field: "labels", headerName: "Labels", flex: 1.2, 
      renderCell: ({ value }) => (
      <Box display="flex" gap={0.5} flexWrap="wrap">
        {(value || "")
          .split(", ")
          .filter(Boolean)
          .slice(0, 3)
          .map((label) => (
            <StatusChip
              key={label}
              label={label}
              tone="default"
            />
          ))}
      </Box>
)
    },
    { field: "taskCount", headerName: "Tasks", flex: 0.5 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <TableActions
          onCosts={!sop ? () => handleCosts(row.id) : undefined}
          onEdit={() => handleEdit(row.id)}
          onDelete={() => handleOpenDeleteModal(row)}
          onCreateTask={
            !sop
              ? () =>
                  navigate("/createTask", {
                    state: {
                      projectId: row.id,
                      redirectPath,
                    },
                  })
              : undefined
          }
          hideCreate={sop}
        />
      ),
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header
          title={`MANAGE ${String(name).toUpperCase()}S`}
          subtitle={
            tenantName
              ? `Manage ${String(name).toLowerCase()}s for ${tenantName}`
              : `Manage ${String(name).toLowerCase()}s for the selected tenant`
          }
        />
        <ActionButton
          tone="gold"
          onClick={() =>
            navigate("/createProject", {
              state: {
                sop,
                name,
                redirectPath,
              },
            })
          }
          disabled={!tenantId}
        >
          Create {name}
        </ActionButton>
      </Box>

      {!tenantId ? (
        <Box mt={4}>
          <Typography variant="h6">
            Select a tenant in the top bar to view {String(name).toLowerCase()}s.
          </Typography>
        </Box>
      ) : (
        <ThemedDataGrid rows={projects} columns={columns} />
      )}

      <DeleteProjectModal
        open={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        project={selectedProject}
      />
    </Box>
  );
};

export default ManageProjects;